import { useCallback, useContext, useEffect, useRef } from 'react';

import { Slot } from '@radix-ui/react-slot';

import ChatbotContext from './ChatbotContext';
import { CHATBOT_DEFAULT_BOTTOM_OFFSET, CHATBOT_DEFAULT_RIGHT_OFFSET, CHATBOT_SMALL_VIEW_MAX_WIDTH } from './constants';

type Props = {
  asChild?: boolean;
  chatButtonPlacement?: 'above' | 'right';
  children: React.ReactNode;
} & React.ComponentProps<'div'>;

const ChatbotPositioningElement = ({ asChild, chatButtonPlacement = 'above', ...props }: Props) => {
  const ref = useRef<HTMLDivElement>(null);

  const isChatLoaded = useContext(ChatbotContext);
  const adjust = useCallback(() => {
    if (isChatLoaded && ref.current) {
      const rect = ref.current?.getBoundingClientRect();
      let repositioningEvent: undefined | ({ data?: Record<string, unknown> } & Event);

      if (chatButtonPlacement === 'above') {
        const top = rect?.y || window.innerHeight;
        const offset = window.innerWidth > CHATBOT_SMALL_VIEW_MAX_WIDTH ? 0 : CHATBOT_DEFAULT_BOTTOM_OFFSET;
        const bottom = window.innerHeight - top + offset;
        repositioningEvent = new Event('Chatbot.repositioning');
        repositioningEvent.data = { bottom };
      } else if (chatButtonPlacement === 'right') {
        const left = rect?.x || window.innerWidth;
        const right = window.innerWidth - left + CHATBOT_DEFAULT_RIGHT_OFFSET;
        repositioningEvent = new Event('Chatbot.repositioning');
        repositioningEvent.data = { right };
      }

      if (repositioningEvent) {
        window.dispatchEvent(repositioningEvent);
      }
    }
  }, [chatButtonPlacement, isChatLoaded]);

  useEffect(() => {
    adjust();
    window.addEventListener('resize', adjust);
    const observer = new MutationObserver(adjust);
    if (ref.current) {
      observer.observe(ref.current, { attributes: true, childList: true, subtree: true });
    }
    return () => {
      window.removeEventListener('resize', adjust);
      observer.disconnect();
    };
  }, [adjust]);

  useEffect(
    () => () => {
      window.dispatchEvent(new Event('Chatbot.restorePositioning'));
    },
    [],
  );

  const Component = asChild ? Slot : 'div';
  return <Component {...props} ref={ref} />;
};

export default ChatbotPositioningElement;
